import VisualizationBuilderModel from "../tracked/visualizationBuilderModel";
import Guid from "../../helpers/guid";
import Visualization from "./visualization";
import IRepo from "../../interfaces/repo";
import Repo from "../../state/modules/repo";
import {
  VisualizationDataTableFilterCapability,
  VisualizationDataTableFilterCapabilityManager
} from "~/models/visualizations/visualizationDataTableFilterCapability";
import {
  VisualizationDataTableFilterLayoutType,
  VisualizationDataTableFilterType
} from "~/enums/visualizations/visualizationDataTableFilterType";
import {ISelectedProperty} from "~/state/modules/singleSelectedProperty";
import Selected from "../../../formability/visualizationBuilder/store/modules/selected";
import VisualizationDataTableFilterDefinition from "~/models/visualizations/visualizationDataTableFilterDefinition";
import {Hash} from "~/interfaces/hashInterfaces";
import {ObjectHelper} from "~/helpers/objectHelper";
import ElementHandler from "~/services/elementHandler";
import {RelationType} from "~/models/relationType";
import {IMultiSourceConditionalRuleContainer} from "~/models/conditionalRules/conditionalRuleContainer";
import MultiSourceConditionalRuleSet from "~/models/conditionalRules/multiSourceConditionalRuleSet";
import IConditionalRuleReferencedControl from "~/models/conditionalRules/referencedControl";
import {ModelType} from "~/enums/modelType";
import VisualizationSourceForm from "~/models/visualizations/visualizationSourceForm";
import VisualizationSourceObjectModel from "~/models/visualizations/visualizationSourceObjectModel";
import {ConditionalFieldDefinition} from "~/models/conditionalRules/conditionalFieldDefinition";
import VisualizationFilterConditionalFields from "~/models/visualizations/visualizationFilterConditionalFields";
import ConditionalRuleSetFactory from "~/models/conditionalRules/conditionalRuleSetFactory";
import {DateFilterMode} from "./dateFilterMode";
import {DurationUnit} from "~/enums/durationUnit";
import {ObjectModelBuiltinPropertyType} from "~/enums/objectModelBuiltinPropertyType";
import {FormBuiltinType} from "~/enums/formBuiltinType";
import VisualizationDataTableFilterReference from "~/models/visualizations/visualizationDataTableFilterReference";
import {SearchMatchType} from "~/enums/searchMatchType";
import {VerticalAlignment} from "../../enums/verticalAlignment";
import Vue from "vue";

export default class VisualizationDataTableFilter extends VisualizationBuilderModel implements IMultiSourceConditionalRuleContainer {
  static repo: IRepo<VisualizationDataTableFilter> = new Repo<VisualizationDataTableFilter>('visualizationDataTableFilters', VisualizationDataTableFilter, {});
  repo: IRepo<this>;

  rootModel: Visualization;
  filter_type: VisualizationDataTableFilterType;
  sort_order: number;
  data_table_filters: Hash<VisualizationDataTableFilter> = {};
  label_title: string;
  date_filter_mode: DateFilterMode;
  rolling_range_unit: DurationUnit;
  rolling_range_amount: number;
  source_client_id: string | null;
  element_client_id: string | null;
  data_fusion_point_client_id: string | null;
  object_model_builtin_type: ObjectModelBuiltinPropertyType | null;
  form_builtin_type: FormBuiltinType | null;
  placeholder_text: string;
  filter_references: Hash<VisualizationDataTableFilterReference> = {};
  match_type: SearchMatchType;
  help_text: string;
  layout_config: number[];
  vertical_alignment: VerticalAlignment;

  constructor() {
    super();
    this.defineProperty('client_id', Guid.create());

    for (const definition of VisualizationFilterConditionalFields.FILTER_FIELDS) {
      const conditionalFieldName = `${definition.fieldName}_conditional_rule_sets`;
      this.except.push(conditionalFieldName);
      this[conditionalFieldName] = {};
      this.createEmbeddedObject[conditionalFieldName] = (parent, json) => {
        return ConditionalRuleSetFactory.createForContainer(this, definition, {}, true, false);
      };
      if (definition.defaultConditionalValue instanceof Function) {
        this.except.push(definition.fieldName);
        this.createEmbeddedObject[definition.fieldName] = definition.defaultConditionalValue;
      }
    }
  }

  initialize(rootModel: Visualization, parent: Visualization, type: number, json: object = {}): this {
    this.rootModel = rootModel;
    this.parent = parent;
    this.defineProperty('filter_type', type);
    this.defineProperty('sort_order', 0);
    VisualizationDataTableFilterCapabilityManager.addDefaultProperties(this);

    this.loadFromJson(json);

    return this;
  }

  get EMBEDDED_MODELS(): { [key in keyof VisualizationDataTableFilter]?: { type: any, relationType: RelationType } } {
    const models = {
      'data_table_filters': { type: VisualizationDataTableFilter, relationType: RelationType.dictionary },
      'filter_references': { type: VisualizationDataTableFilterReference, relationType: RelationType.dictionary },
    };
    for (const definition of VisualizationFilterConditionalFields.FILTER_FIELDS) {
      models[`${definition.fieldName}_conditional_rule_sets`] = {type: MultiSourceConditionalRuleSet, relationType: RelationType.dictionary};
      if (definition.defaultConditionalValue instanceof Function) {
        models[definition.fieldName] = {type: Object, relationType: RelationType.singleObject};
      }
    }
    return models;
  }

  createEmbeddedObject = {
    data_table_filters: (parent, json) => {
      return VisualizationDataTableFilter.repo.findOrCreate(json.id, json.client_id);
    },
    filter_reference: (parent, json) => {
      return VisualizationDataTableFilterReference.repo.findOrCreate(json.id, json.client_id);
    },
  }

  get rootName(): string {
    return 'data_table_filters';
  }

  addToRepo(): void {
    VisualizationDataTableFilter.repo.add(this);
  }

  get selector(): ISelectedProperty {
    return Selected.selectedDataTableFilter;
  }

  select(): void {
    this.selector.set(this);
  }

  deselect(): void {
    this.selector.deselectIf(this);
  }

  get isSelected(): boolean {
    return this.selector.is(this);
  }

  get isSelectable(): boolean {
    return this.can(VisualizationDataTableFilterCapability.selectable);
  }

  can(capability: VisualizationDataTableFilterCapability): boolean {
    return VisualizationDataTableFilterCapabilityManager.can(this.filter_type, capability);
  }

  is(...types: VisualizationDataTableFilterType[]): boolean {
    return types.includes(this.filter_type);
  }

  get displayName(): string {
    return VisualizationDataTableFilterDefinition.getDisplayName(this.filter_type);
  }

  get dataTableFiltersArray(): VisualizationDataTableFilter[] {
    return ObjectHelper.toArray(this.data_table_filters).sort((a, b) => a.sort_order - b.sort_order);
  }
  _dataTableFiltersHandler = new ElementHandler(this, VisualizationDataTableFilter, 'data_table_filters');
  get dataTableFiltersHandler(): ElementHandler {
    return this._dataTableFiltersHandler;
  }

  get filterReferencesArray(): VisualizationDataTableFilterReference[] {
    return ObjectHelper.toArray(this.filter_references);
  }

  afterDestroy() {
    super.afterDestroy();
    this.parent.dataTableFiltersHandler.recalculateElementPositions();
  }

  get isLast(): boolean {
    return this.sort_order === this.parent.dataTableFiltersHandler.lastSortOrder();
  }

  get isFirst(): boolean {
    return this.sort_order <= 0;
  }

  moveUp(): void {
    if (!this.isFirst) {
      this.sort_order--;
      this.parent.dataTableFiltersHandler.updateElementPositions(this.parent, this, this.sort_order);
    }
  }

  moveDown(): void {
    if (!this.isLast) {
      this.sort_order++;
      this.parent.dataTableFiltersHandler.updateElementPositions(this.parent, this, this.sort_order);
    }
  }

  // ******************** CONDITIONAL RULE CONTAINER ******************** //

  getConditionalRuleSets(fieldName: string): Hash<MultiSourceConditionalRuleSet> {
    return this[`${fieldName}_conditional_rule_sets`];
  }

  getSortedConditionalRuleSets(fieldName: string): MultiSourceConditionalRuleSet[] {
    const ruleSets = this.getConditionalRuleSets(fieldName);
    return ObjectHelper.toArray(ruleSets).sort((a, b) => a.sort_order - b.sort_order);
  }

  createNewRuleSet(fieldName: string, definition: ConditionalFieldDefinition): MultiSourceConditionalRuleSet {
    return new MultiSourceConditionalRuleSet(fieldName, definition, this);
  }

  getReferencedControl(id: string): IConditionalRuleReferencedControl {
    const fusionPoints = this.rootModel.dataFusionPointsArray;
    for (const point of fusionPoints) {
      if (point.client_id === id) {
        return point;
      }
    }

    for (const source of this.rootModel.visualizationSources) {
      switch (source.friendly_type) {
        case ModelType.Form:
          const form = (source as VisualizationSourceForm).source;
          for (const el of form.referenceableFormElementsFor(null)) {
            if (el.client_id === id) {
              return el;
            }
          }
          break;

        case ModelType.ObjectModel:
          const model = (source as VisualizationSourceObjectModel).source;
          for (const prop of model.objectModelPropertiesArray) {
            if (prop.client_id === id) {
              return prop;
            }
          }
          break;

        default:
          break;
      }
    }

    return null;
  }

  getReferencableControls(): IConditionalRuleReferencedControl[] {
    const grouped = this.getGroupedReferencableControls();
    const array = [];
    for (const group of Object.values(grouped)) {
      array.push(...group);
    }
    return array;
  }

  getGroupedReferencableControls(): {[key: string]: IConditionalRuleReferencedControl[]} {
    const controls = {};
    const fusionPoints = this.rootModel.dataFusionPointsArray;
    if (fusionPoints.length > 0) {
      controls['Fused Data Points'] = fusionPoints;
    }
    for (const source of this.rootModel.visualizationSources) {
      switch (source.friendly_type) {
        case ModelType.Form:
          const form = (source as VisualizationSourceForm).source;
          const elements = form.referenceableFormElementsFor(null).filter(e => form.isElementIndexed(e));
          controls[form.title] = elements;
          break;

        case ModelType.ObjectModel:
          const model = (source as VisualizationSourceObjectModel).source;
          const properties = model.objectModelPropertiesArray.filter(p => model.isPropertyIndexed(p));
          controls[model.title] = properties;
          break;

        default:
          break;
      }
    }
    return controls;
  }

  // ******************** END CONDITIONAL RULE CONTAINER ******************** //

  get isExpandable(): boolean {
    return this.is(VisualizationDataTableFilterLayoutType.Grid);
  }

  unpublishedSources(): boolean {
    if (this.is(VisualizationDataTableFilterLayoutType.Grid) || this.is(VisualizationDataTableFilterLayoutType.Stack)) {
      return this.dataTableFiltersArray.some(f => f.unpublishedSources);
    } else {
      if (this.can(VisualizationDataTableFilterCapability.dataPointReferencable) && this.source_client_id) {
        const source = this.rootModel.visualizationSources.find((s) => s.client_id === this.source_client_id);
        if (!source.isPublished) {
          return true;
        }
      }
      if (this.can(VisualizationDataTableFilterCapability.multiDataPointReferencable)) {
        for (const reference of this.filterReferencesArray) {
          if (!reference.source_client_id) {
            continue;
          }
          const source = this.rootModel.visualizationSources.find((s) => s.client_id === reference.source_client_id);
          if (!source.isPublished) {
            return true;
          }
        }
      }
      return false;
    }
  }

  initializeEmbedded(parent: this, item: any, position: number, json: object = {}, selectItem: boolean = true) {
    if (item instanceof VisualizationDataTableFilter) {
      if (parent.filter_type !== VisualizationDataTableFilterLayoutType.Grid) {
        item.dataTableFiltersHandler.updateElementPositions(parent, item, position);
      }

      parent.trackCreationDeletion(item.client_id, null, item.asJson(), item);
      item.addToRepo();
      item.select();

      Vue.set(parent[item.rootName], item.client_id, item);
      return item;
    }
  }

}

